/**
 * SecureEmbed - Ingestion Wizard
 *
 * 5-step wizard for creating/editing ingestion jobs.
 * Steps: (1) Target & Volume, (2) Sources, (3) Topics & Proportions,
 * (4) Advanced, (5) Review & Action.
 *
 * Replaces CreateIngestionJob.tsx and DataIngestion.tsx.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Database, ChevronRight, ChevronLeft, Rocket, Save, Clock,
  AlertCircle, CheckCircle, Settings, Eye, Loader2,
  X, Plus, Sliders, Calendar,
} from 'lucide-react';
import { DATASTORE_CARDS, DATASTORE_COLORS } from '../constants/datastores';
import { getSourcesForDatastore, type SourceEmphasis } from '../constants/ingestionSources';
import { jobsApi } from '../services/api';

const SIZE_PRESETS = [
  { label: '100', value: 100 },
  { label: '1K', value: 1_000 },
  { label: '10K', value: 10_000 },
  { label: '100K', value: 100_000 },
  { label: '500K', value: 500_000 },
  { label: '1M', value: 1_000_000 },
];

const EMBEDDING_MODELS = [
  { id: 'amazon.titan-embed-text-v2:0', label: 'Titan Embed V2', dims: 1024 },
  { id: 'amazon.titan-embed-text-v1', label: 'Titan Embed V1', dims: 1536 },
];

// Datastore → target type mapping. Datastores not listed here show the manual selector.
const DATASTORE_TARGET_TYPE: Record<string, string> = {
  // Most datastores use OpenSearch for embeddings/vector search
  'aemo': 'opensearch',
  'aemo-duid': 'opensearch',
  'aemo-fcas': 'opensearch',
  'bom': 'opensearch',
  'aer': 'opensearch',
  'regulatory': 'opensearch',
  'scholarly': 'opensearch',
  'partner': 'opensearch',
  'medicine': 'opensearch',
  'curriculum': 'opensearch',
  'education-resources': 'opensearch',
  'tertiary-resources': 'opensearch',
  'food-interactions': 'opensearch',
  'innovateflow': 'opensearch',
  'insurance-apra-grf': 'opensearch',
  'insurance-apra-lrf': 'opensearch',
  'insurance-apra-hrf': 'opensearch',
  'insurance-premium-adequacy': 'opensearch',
  'insurance-claims-triage': 'opensearch',
  'insurance-afca-complaints': 'opensearch',
  'insurance-state-schemes': 'opensearch',
  'insurance-cyber-threats': 'opensearch',
  'insurance-bom-weather': 'opensearch',
  'insurance-climate-projections': 'opensearch',
  // Structured data → DynamoDB
  'insurance-actuarial-triangles': 'dynamodb',
  // Raw file storage → S3 data lake
  'insurance-bordereaux': 's3-datalake',
};

interface SourceAllocation {
  key: string;
  label: string;
  emphasis?: string;
  description?: string;
  enabled: boolean;
  percentage: number;
}

interface TopicAllocation {
  key: string;
  label: string;
  percentage: number;
}

interface WizardState {
  target_database: string;
  target_type: string;
  total_target_count: number;
  custom_count: string;
  mode: 'incremental' | 'bulk';
  sources: SourceAllocation[];
  topics: TopicAllocation[];
  topic_input: string;
  job_name: string;
  cleanup_raw_data: boolean;
  embedding_model: string;
  embedding_dims: number;
  schedule_expression: string;
  custom_cron: string;
}

const initialState: WizardState = {
  target_database: '',
  target_type: 'opensearch',
  total_target_count: 10_000,
  custom_count: '',
  mode: 'bulk',
  sources: [],
  topics: [],
  topic_input: '',
  job_name: '',
  cleanup_raw_data: false,
  embedding_model: 'amazon.titan-embed-text-v2:0',
  embedding_dims: 1024,
  schedule_expression: '',
  custom_cron: '',
};

export default function IngestionWizard() {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const preselectedDatastore = searchParams.get('datastore') || '';

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardState>({
    ...initialState,
    target_database: preselectedDatastore,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!jobId);

  const update = useCallback((partial: Partial<WizardState>) => {
    setForm(prev => ({ ...prev, ...partial }));
    setError(null);
  }, []);

  // Load existing job for edit mode
  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const job = await jobsApi.getJob(jobId);
        const availableSources = getSourcesForDatastore(job.target_database);
        const sourceProp = (job as any).source_proportions || {};
        const topicProp = (job as any).topic_proportions || {};

        setForm({
          target_database: job.target_database,
          target_type: job.target_type || 'opensearch',
          total_target_count: job.total_target_count,
          custom_count: '',
          mode: job.mode,
          sources: availableSources.map(s => ({
            key: s.key,
            label: s.label,
            emphasis: s.emphasis,
            description: s.description,
            enabled: sourceProp[s.key] !== undefined ? true : s.defaultEnabled,
            percentage: Number(sourceProp[s.key]) || 0,
          })),
          topics: Object.entries(topicProp).map(([key, pct]) => ({
            key,
            label: key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            percentage: Number(pct) || 0,
          })),
          topic_input: '',
          job_name: (job as any).job_name || '',
          cleanup_raw_data: (job as any).cleanup_raw_data ?? false,
          embedding_model: (job as any).embedding_config?.model_id || 'amazon.titan-embed-text-v2:0',
          embedding_dims: (job as any).embedding_config?.dimensions || 1024,
          schedule_expression: (job as any).schedule_expression || '',
          custom_cron: '',
        });
      } catch (err: any) {
        setError('Failed to load job');
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);

  // Auto-set target_type when datastore changes (if mapping exists)
  useEffect(() => {
    if (jobId) return;
    if (!form.target_database) return;
    const mapped = DATASTORE_TARGET_TYPE[form.target_database];
    if (mapped) {
      update({ target_type: mapped });
    }
  }, [form.target_database, jobId, update]);

  // Auto-select mode based on volume
  const autoMode = form.total_target_count > 10_000 ? 'bulk' : 'incremental';
  useEffect(() => {
    update({ mode: autoMode });
  }, [form.total_target_count, autoMode, update]);

  const datastoreLabel = DATASTORE_CARDS.find(d => d.key === form.target_database)?.label || form.target_database;
  const datastoreColor = DATASTORE_COLORS[form.target_database]?.hex || '#6B7280';
  const hasKnownTargetType = !!DATASTORE_TARGET_TYPE[form.target_database];

  const STEPS = [
    { id: 'datastore', label: 'Datastore' },
    { id: 'sources', label: 'Sources' },
    { id: 'topics', label: 'Topics' },
    { id: 'settings', label: 'Settings' },
    { id: 'review', label: 'Review' },
  ];

  const SCHEDULE_PRESETS = [
    { label: 'Every hour', value: 'rate(1 hour)' },
    { label: 'Every 6 hours', value: 'rate(6 hours)' },
    { label: 'Daily', value: 'rate(1 day)' },
    { label: 'Weekly', value: 'rate(7 days)' },
    { label: 'Monthly', value: 'rate(30 days)' },
  ];

  const buildConfig = (): any => ({
    target_database: form.target_database,
    target_type: form.target_type,
    total_target_count: form.total_target_count,
    mode: form.mode,
    job_name: form.job_name,
    cleanup_raw_data: form.cleanup_raw_data,
    embedding_config: {
      model_id: form.embedding_model,
      dimensions: form.embedding_dims,
    },
    source_proportions: Object.fromEntries(
      form.sources.filter(s => s.enabled).map(s => [s.key, s.percentage])
    ),
    topic_proportions: Object.fromEntries(
      form.topics.map(t => [t.key, t.percentage])
    ),
    schedule_expression: form.schedule_expression || '',
  });

  const canNext = () => {
    if (step === 0) return !!form.target_database;
    if (step === 1) return form.sources.some(s => s.enabled);
    return true;
  };

  // Source allocation helpers
  const toggleSource = (key: string) => {
    update({
      sources: form.sources.map(s =>
        s.key === key ? { ...s, enabled: !s.enabled } : s
      ),
    });
  };

  const updateSourcePct = (key: string, pct: number) => {
    update({
      sources: form.sources.map(s =>
        s.key === key ? { ...s, percentage: pct } : s
      ),
    });
  };

  const sourcePctTotal = form.sources
    .filter(s => s.enabled)
    .reduce((sum, s) => sum + s.percentage, 0);

  const EMPHASIS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
    general: { label: 'General', color: 'text-blue-700', bg: 'bg-blue-100' },
    medical: { label: 'Medical', color: 'text-green-700', bg: 'bg-green-100' },
    science: { label: 'Science', color: 'text-purple-700', bg: 'bg-purple-100' },
    funding: { label: 'Funding', color: 'text-amber-700', bg: 'bg-amber-100' },
    patent: { label: 'Patent', color: 'text-cyan-700', bg: 'bg-cyan-100' },
    primary: { label: 'Primary', color: 'text-blue-700', bg: 'bg-blue-100' },
    secondary: { label: 'Secondary', color: 'text-violet-700', bg: 'bg-violet-100' },
    supplementary: { label: 'Supplementary', color: 'text-gray-600', bg: 'bg-gray-100' },
  };

  // Topic helpers
  const suggestedTopics = (form.target_database
    ? [
        { key: 'general', label: 'General' },
        { key: 'technical', label: 'Technical' },
        { key: 'regulatory', label: 'Regulatory' },
        { key: 'financial', label: 'Financial' },
        { key: 'operational', label: 'Operational' },
      ]
    : []
  ).filter(t => !form.topics.some(ft => ft.key === t.key));

  const addTopic = (key: string, label?: string) => {
    const resolvedLabel = label || key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    update({
      topics: [...form.topics, { key, label: resolvedLabel, percentage: 0 }],
    });
  };

  const removeTopic = (key: string) => {
    update({
      topics: form.topics.filter(t => t.key !== key),
    });
  };

  const updateTopicPct = (key: string, pct: number) => {
    update({
      topics: form.topics.map(t =>
        t.key === key ? { ...t, percentage: pct } : t
      ),
    });
  };

  const handleAddCustomTopic = () => {
    const key = form.topic_input.trim().toLowerCase().replace(/\s+/g, '-');
    if (key && !form.topics.some(t => t.key === key)) {
      addTopic(key);
      update({ topic_input: '' });
    }
  };

  const topicPctTotal = form.topics.reduce((sum, t) => sum + t.percentage, 0);

  const handleRunNow = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const config = buildConfig();
      const result = await jobsApi.createJob(config);
      await jobsApi.startJob(result.job_id);
      setSuccess('Job started! Redirecting to job detail...');
      setTimeout(() => navigate(`/ingestion/jobs/${result.job_id}`), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to start job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSchedule = async () => {
    const expr = form.schedule_expression || form.custom_cron;
    if (!expr) {
      setError('Please select or enter a schedule');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const config = buildConfig();
      config.schedule_expression = expr;
      const result = await jobsApi.createJob(config);
      await jobsApi.scheduleJob(result.job_id, expr);
      setSuccess('Job scheduled! Redirecting to jobs list...');
      setTimeout(() => navigate('/ingestion/jobs'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to schedule job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveForLater = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const config = buildConfig();
      (config as any).status = 'DRAFT';
      await jobsApi.createJob(config);
      setSuccess('Job saved! Redirecting to jobs list...');
      setTimeout(() => navigate('/ingestion/jobs'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-500">Loading job configuration...</span>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-4">
        <Link to="/" className="hover:text-gray-700">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/ingestion/jobs" className="hover:text-gray-700">Ingestion Jobs</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-gray-900 font-medium">
          {jobId ? `Edit: ${form.job_name || 'Ingestion Job'}` : 'New Ingestion'}
        </span>
      </nav>

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Database className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">
          {jobId ? `Edit: ${form.job_name || 'Ingestion Job'}` : 'New Ingestion'}
        </h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1 mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              onClick={() => i < step ? setStep(i) : undefined}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                i === step
                  ? 'bg-blue-100 text-blue-700'
                  : i < step
                  ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {i < step ? <CheckCircle className="w-4 h-4" /> : null}
              <span>{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">

        {/* Step 1: Target & Volume */}
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Database <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {DATASTORE_CARDS.filter(d => d.key !== 'audit-trail').map(d => {
                  const Icon = d.icon;
                  const color = DATASTORE_COLORS[d.key]?.hex || '#6B7280';
                  const selected = form.target_database === d.key;
                  return (
                    <button
                      key={d.key}
                      onClick={() => update({ target_database: d.key })}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left transition-all ${
                        selected
                          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                      <span className="truncate">{d.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Record Count
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {SIZE_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => update({ total_target_count: p.value, custom_count: '' })}
                    className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                      form.total_target_count === p.value && !form.custom_count
                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Custom:</span>
                <input
                  type="number"
                  min={1}
                  placeholder="Enter count"
                  value={form.custom_count}
                  onChange={e => {
                    const val = e.target.value;
                    update({
                      custom_count: val,
                      total_target_count: val ? parseInt(val, 10) : 10_000,
                    });
                  }}
                  className="w-40 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <span className="text-xs text-gray-500">Mode auto-selected: </span>
              <span className="text-xs font-medium text-gray-700">
                {autoMode === 'incremental' ? 'Incremental (Step Functions) — best for ≤10K' : 'Bulk (EC2) — best for 10K+'}
              </span>
            </div>
          </div>
        )}

        {/* Step 2: Sources */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Sliders className="w-4 h-4 inline mr-1" />
                Data Sources for {datastoreLabel}
              </label>
              <p className="text-xs text-gray-500 mb-4">
                Select which sources to ingest from and set the percentage allocation per source.
                Percentages should sum to 100%.
              </p>

              {form.sources.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No sources configured for this datastore.</p>
              ) : (
                <div className="space-y-4">
                  {/* Group sources by emphasis */}
                  {(['general', 'medical', 'science', 'funding', 'patent'] as SourceEmphasis[])
                    .filter(emp => form.sources.some(s => s.emphasis === emp))
                    .map(emp => {
                      const cfg = EMPHASIS_CONFIG[emp];
                      const groupSources = form.sources.filter(s => s.emphasis === emp);
                      return (
                        <div key={emp}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>
                              {cfg.label}
                            </span>
                            <span className="text-xs text-gray-400">
                              {groupSources.filter(s => s.enabled).length}/{groupSources.length} enabled
                            </span>
                          </div>
                          <div className="space-y-1 ml-1">
                            {groupSources.map(source => (
                              <div key={source.key} className={`rounded-md border px-3 py-2 transition-colors ${
                                source.enabled
                                  ? 'border-blue-200 bg-blue-50/30'
                                  : 'border-gray-100 bg-white'
                              }`}>
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={source.enabled}
                                    onChange={() => toggleSource(source.key)}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm font-medium ${source.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                                      {source.label}
                                    </span>
                                    <p className={`text-xs mt-0.5 leading-snug ${source.enabled ? 'text-gray-500' : 'text-gray-300'}`}>
                                      {source.description}
                                    </p>
                                  </div>
                                  {source.enabled && (
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        value={source.percentage}
                                        onChange={e => updateSourcePct(source.key, parseInt(e.target.value))}
                                        className="w-20"
                                      />
                                      <span className="text-sm text-gray-600 w-10 text-right">{source.percentage}%</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  <div className={`text-sm font-medium mt-2 ${
                    sourcePctTotal === 100 ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    Total: {sourcePctTotal}% {sourcePctTotal !== 100 && '(should be 100%)'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Topics & Proportions */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topic Areas
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select suggested topics or add your own. Set percentage allocation per topic.
              </p>

              {/* Suggested topics */}
              {suggestedTopics.length > 0 && (
                <div className="mb-4">
                  <span className="text-xs text-gray-500 font-medium">Suggested:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {suggestedTopics.map(topic => {
                      const added = form.topics.some(t => t.key === topic.key);
                      return (
                        <button
                          key={topic.key}
                          onClick={() => addTopic(topic.key, topic.label)}
                          disabled={added}
                          className="px-2.5 py-1 rounded text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          + {topic.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Free-text topic input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={form.topic_input}
                  onChange={e => update({ topic_input: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCustomTopic())}
                  placeholder="Add custom topic..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddCustomTopic}
                  disabled={!form.topic_input.trim()}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Selected topics with sliders */}
              {form.topics.length > 0 ? (
                <div className="space-y-3">
                  {form.topics.map(topic => (
                    <div key={topic.key} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                      <span className="text-sm text-gray-900 flex-1">{topic.label}</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={topic.percentage}
                        onChange={e => updateTopicPct(topic.key, parseInt(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-10 text-right">{topic.percentage}%</span>
                      <button onClick={() => removeTopic(topic.key)} className="text-gray-400 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className={`text-sm font-medium mt-2 ${
                    topicPctTotal === 100 ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    Total: {topicPctTotal}% {topicPctTotal !== 100 && '(should be 100%)'}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No topics selected. All content will be included.</p>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Advanced */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Job Name</label>
              <input
                type="text"
                value={form.job_name}
                onChange={e => update({ job_name: e.target.value })}
                placeholder={`${datastoreLabel} ingestion`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Settings className="w-4 h-4 inline mr-1" />
                Embedding Model
              </label>
              <div className="space-y-2">
                {EMBEDDING_MODELS.map(m => (
                  <button
                    key={m.id}
                    onClick={() => update({ embedding_model: m.id, embedding_dims: m.dims })}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-md border text-left ${
                      form.embedding_model === m.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div>
                      <div className="text-sm font-medium">{m.label}</div>
                      <div className="text-xs text-gray-500">{m.dims} dimensions</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <div className="text-sm font-medium text-gray-700">Cleanup Raw Data</div>
                <div className="text-xs text-gray-500">Delete raw source files after indexing</div>
              </div>
              <button
                onClick={() => update({ cleanup_raw_data: !form.cleanup_raw_data })}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  form.cleanup_raw_data ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    form.cleanup_raw_data ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>

            {!hasKnownTargetType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Type</label>
                <p className="text-xs text-gray-500 mb-2">
                  Could not auto-determine the storage backend for this datastore. Please select manually.
                </p>
                <div className="flex gap-2">
                  {['opensearch', 'dynamodb', 's3-datalake'].map(t => (
                    <button
                      key={t}
                      onClick={() => update({ target_type: t })}
                      className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                        form.target_type === t
                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Review & Action */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold">Review & Action</h2>
            </div>

            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-700">{success}</span>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            <div className="bg-gray-50 rounded-md p-4 space-y-3">
              <ReviewRow label="Job Name" value={form.job_name || `${datastoreLabel} ingestion`} />
              <ReviewRow label="Target Database" value={datastoreLabel} color={datastoreColor} />
              <ReviewRow label="Target Type" value={form.target_type} />
              <ReviewRow label="Record Count" value={form.total_target_count.toLocaleString()} />
              <ReviewRow label="Mode" value={form.mode === 'incremental' ? 'Incremental (Step Functions)' : 'Bulk (EC2)'} />
              <ReviewRow label="Embedding Model" value={EMBEDDING_MODELS.find(m => m.id === form.embedding_model)?.label || form.embedding_model} />
              <ReviewRow
                label="Sources"
                value={form.sources.filter(s => s.enabled).map(s => `${s.label} (${s.percentage}%)`).join(', ') || 'None'}
              />
              <ReviewRow
                label="Topics"
                value={form.topics.map(t => `${t.label} (${t.percentage}%)`).join(', ') || 'All content'}
              />
              <ReviewRow label="Cleanup Raw Data" value={form.cleanup_raw_data ? 'Yes' : 'No'} />
            </div>

            {/* Schedule picker (shown inline) */}
            <div className="border border-gray-200 rounded-md p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Schedule (for Schedule action)
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {SCHEDULE_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => update({ schedule_expression: p.value, custom_cron: '' })}
                    className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                      form.schedule_expression === p.value
                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Custom cron:</span>
                <input
                  type="text"
                  value={form.custom_cron}
                  onChange={e => update({ custom_cron: e.target.value, schedule_expression: '' })}
                  placeholder="cron(0 12 * * ? *)"
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={handleRunNow}
                disabled={submitting || !!success}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                Run Now
              </button>
              <button
                onClick={handleSchedule}
                disabled={submitting || !!success}
                className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                Schedule
              </button>
              <button
                onClick={handleSaveForLater}
                disabled={submitting || !!success}
                className="flex items-center gap-2 px-5 py-2.5 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save for Later
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => step > 0 ? setStep(step - 1) : navigate('/ingestion/jobs')}
          className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-4 h-4" />
          {step > 0 ? 'Back' : 'Cancel'}
        </button>

        {step < 4 && (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canNext()}
            className="flex items-center gap-1 px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%] truncate" style={color ? { color } : undefined}>
        {value}
      </span>
    </div>
  );
}
